import React, { useEffect, useMemo, useState } from "react";
import { DeckertPost, DeckertTeamMember, useCms } from "./CmsProvider";
import { FileText, Users, Loader2, Trash2, Plus, Building, Tag, MapPin, Pencil } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Button } from "../../../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Skeleton } from "../../../components/ui/skeleton";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Separator } from "../../../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { Badge } from "../../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { toast } from "sonner";
import { ImageUploader } from "./ImageUploader";
import { Switch } from "../../../components/ui/switch";

interface CmsAdminPanelProps {
  company: string;
  companiesToLoad?: string[];
}

// Fallback-Firmen, falls nicht via Prop übergeben
const DEFAULT_COMPANIES = ["LAW", "FUFOLI"];
const CATEGORY_OPTIONS = ["Werkstatttage", "Berufsfelderkundung", "Bewerbungshilfe", "Partnerschule"] as const;

const CmsAdminPanel: React.FC<CmsAdminPanelProps> = ({ company, companiesToLoad = DEFAULT_COMPANIES }) => {
  const {
    fetchPosts,
    fetchTeamMembers,
    createPost,
    deletePost,
    createMember,
    deleteMember,
    updatePost,
    updateMember,
    uploadMemberImage,
    removeMemberImage: removeMemberImageApi, // Alias gegen Namenskonflikt mit UI-Flag
  } = useCms();

  const [activeCompany, setActiveCompany] = useState(company);
  const companies = useMemo(
    () => (companiesToLoad.includes(company) ? companiesToLoad : [company, ...companiesToLoad]),
    [company, companiesToLoad]
  );

  // Daten je Firma
  const [postsMap, setPostsMap] = useState<Record<string, DeckertPost[]>>({});
  const [membersMap, setMembersMap] = useState<Record<string, DeckertTeamMember[]>>({});

  // Lade-/Fehlerzustände je Firma
  const [loadingPosts, setLoadingPosts] = useState<Record<string, boolean>>({});
  const [loadingMembers, setLoadingMembers] = useState<Record<string, boolean>>({});
  const [errorPosts, setErrorPosts] = useState<Record<string, string | null>>({});
  const [errorMembers, setErrorMembers] = useState<Record<string, string | null>>({});

  const [editPost, setEditPost] = useState<DeckertPost | null>(null);
  const [editPostForm, setEditPostForm] = useState<Partial<DeckertPost>>({
    title: "",
    category: "",
    description: "",
  });
  const [isUpdatingPost, setIsUpdatingPost] = useState(false);

  // --- Edit: Member ---
  const [editMember, setEditMember] = useState<DeckertTeamMember | null>(null);
  const [editMemberForm, setEditMemberForm] = useState<Partial<DeckertTeamMember>>({
    name: "",
    role: "",
    location: "",
    bio: "",
    quote: "",
  });
  const [editMemberFile, setEditMemberFile] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [isUpdatingMember, setIsUpdatingMember] = useState(false);

  // Create-Form States
  const [newPost, setNewPost] = useState<Partial<DeckertPost>>({ title: "", description: "", category: "" });
  const [newMember, setNewMember] = useState<Partial<DeckertTeamMember>>({
    name: "",
    role: "",
    location: "",
    bio: "",
    quote: "",
  });
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [isSavingPost, setIsSavingPost] = useState(false);
  const [isSavingMember, setIsSavingMember] = useState(false);

  // Delete-Dialog
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "post" | "member";
    id: string;
    title?: string;
    name?: string;
  } | null>(null);

  // Initial + bei Firmenwechsel: selektiertes Unternehmen priorisiert laden
  useEffect(() => {
    const loadCompany = async (comp: string) => {
      setLoadingPosts((p) => ({ ...p, [comp]: true }));
      setLoadingMembers((m) => ({ ...m, [comp]: true }));
      setErrorPosts((e) => ({ ...e, [comp]: null }));
      setErrorMembers((e) => ({ ...e, [comp]: null }));

      try {
        const [p, m] = await Promise.all([fetchPosts(comp), fetchTeamMembers(comp)]);
        setPostsMap((prev) => ({ ...prev, [comp]: p || [] }));
        setMembersMap((prev) => ({ ...prev, [comp]: m || [] }));
      } catch (err) {
        setErrorPosts((e) => ({ ...e, [comp]: "Beiträge konnten nicht geladen werden." }));
        setErrorMembers((e) => ({ ...e, [comp]: "Team-Mitglieder konnten nicht geladen werden." }));
      } finally {
        setLoadingPosts((p) => ({ ...p, [comp]: false }));
        setLoadingMembers((m) => ({ ...m, [comp]: false }));
      }
    };

    loadCompany(activeCompany);

    companies.forEach((comp) => {
      if (comp !== activeCompany && postsMap[comp] === undefined && membersMap[comp] === undefined) {
        loadCompany(comp);
      }
    });
  }, [activeCompany]);

  // Abgeleitete Daten
  const posts = postsMap[activeCompany] || [];
  const teamMembers = membersMap[activeCompany] || [];
  const isLoading = (loadingPosts[activeCompany] || loadingMembers[activeCompany]) ?? false;

  const openEditPost = (p: DeckertPost) => {
    setEditPost(p);
    setEditPostForm({ title: p.title, category: p.category, description: p.description });
  };

  const openEditMember = (m: DeckertTeamMember) => {
    setEditMember(m);
    setEditMemberForm({
      name: m.name,
      role: m.role,
      location: m.location,
      bio: m.bio,
      quote: m.quote ?? "",
    });
    setEditMemberFile(null);
    setRemoveImage(false);
  };

  const handleUpdatePost = async () => {
    if (!editPost) return;
    const { title, category, description } = editPostForm;
    if (!title?.trim() || !category?.trim() || !description?.trim()) {
      toast.error("Fehlende Angaben", { description: "Bitte Titel, Kategorie und Beschreibung ausfüllen." });
      return;
    }

    try {
      setIsUpdatingPost(true);
      const updated = await updatePost(editPost.id, { title, category, description });
      if (!updated) {
        toast.error("Fehler", { description: "Post konnte nicht gespeichert werden." });
        return;
      }
      setPostsMap((prev) => ({
        ...prev,
        [activeCompany]: (prev[activeCompany] || []).map((p) => (p.id === updated.id ? updated : p)),
      }));
      setEditPost(null);
      toast.success("Gespeichert", { description: `Post „${updated.title}” wurde aktualisiert.` });
    } finally {
      setIsUpdatingPost(false);
    }
  };

  const handleUpdateMember = async () => {
    if (!editMember) return;
    const { name, role, location, bio, quote } = editMemberForm;
    if (!name?.trim() || !role?.trim() || !location?.trim() || !bio?.trim()) {
      toast.error("Fehlende Angaben", { description: "Bitte Name, Rolle, Standort und Bio ausfüllen." });
      return;
    }

    try {
      setIsUpdatingMember(true);

      // 1) Textfelder aktualisieren
      let current = await updateMember(editMember.id, {
        name,
        role,
        location,
        bio,
        quote: quote?.trim() ? quote : null,
      });
      if (!current) {
        toast.error("Fehler", { description: "Mitglied konnte nicht gespeichert werden." });
        return;
      }

      // 2) Bild-Änderungen
      if (editMemberFile) {
        // neues Bild → upload
        const updated = await uploadMemberImage(editMember.id, editMemberFile);
        current = updated ?? current;
        if (!updated) toast.message("Bild-Upload fehlgeschlagen. Textdaten wurden gespeichert.");
      } else if (removeImage && current.profileImageUrl) {
        // bestehendes Bild entfernen
        const ok = await removeMemberImageApi(editMember.id);
        if (ok) {
          current = { ...current, profileImageUrl: null };
        } else {
          toast.message("Bild konnte nicht entfernt werden. Textdaten wurden gespeichert.");
        }
      }

      // 3) UI-Listeneintrag ersetzen
      setMembersMap((prev) => ({
        ...prev,
        [activeCompany]: (prev[activeCompany] || []).map((m) => (m.id === current!.id ? current! : m)),
      }));

      setEditMember(null);
      setEditMemberFile(null);
      setRemoveImage(false);

      toast.success("Gespeichert", { description: `Mitglied „${current.name}” wurde aktualisiert.` });
    } finally {
      setIsUpdatingMember(false);
    }
  };

  // Create: Post
  const handleCreatePost = async () => {
    const { title, description, category } = newPost;
    if (!title?.trim() || !description?.trim() || !category?.trim()) {
      toast.error("Fehlende Angaben", { description: "Bitte Titel, Kategorie und Beschreibung ausfüllen." });
      return;
    }
    try {
      setIsSavingPost(true);
      const created = await createPost({ ...newPost, company: activeCompany });
      if (!created) {
        toast.error("Fehler", { description: "Post konnte nicht erstellt werden." });
        return;
      }
      setPostsMap((prev) => ({ ...prev, [activeCompany]: [...(prev[activeCompany] || []), created] }));
      setNewPost({ title: "", description: "", category: "" });
      toast.success("Erstellt", { description: `Post „${created.title}” wurde gespeichert.` });
    } finally {
      setIsSavingPost(false);
    }
  };

  // Create: Member
  const handleCreateMember = async () => {
    const { name, role, location, bio, quote } = newMember;
    if (!name?.trim() || !role?.trim() || !location?.trim() || !bio?.trim()) {
      toast.error("Fehlende Angaben", { description: "Bitte Name, Rolle, Standort und Bio ausfüllen." });
      return;
    }

    try {
      setIsSavingMember(true);

      // 1) Member anlegen (ohne Bild)
      const created = await createMember({
        company: activeCompany,
        name,
        role,
        location,
        bio,
        quote: quote?.trim() ? quote : null,
      });

      if (!created) {
        toast.error("Fehler", { description: "Mitglied konnte nicht erstellt werden." });
        return;
      }

      let finalMember = created;

      // 2) Falls Bild gewählt: separat hochladen
      if (profileImageFile) {
        const updated = await uploadMemberImage(created.id, profileImageFile);
        if (!updated) {
          toast.message("Bild-Upload fehlgeschlagen. Mitglied ist erstellt.");
        } else {
          finalMember = updated;
        }
      }

      // 3) UI-Listen updaten & Formular zurücksetzen
      setMembersMap((prev) => ({
        ...prev,
        [activeCompany]: [...(prev[activeCompany] || []), finalMember],
      }));
      setNewMember({ name: "", role: "", location: "", bio: "", quote: "" });
      setProfileImageFile(null);

      toast.success("Erstellt", { description: `Mitglied „${finalMember.name}” wurde gespeichert.` });
    } finally {
      setIsSavingMember(false);
    }
  };

  // Delete
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { type, id } = deleteTarget;

    try {
      if (type === "post") {
        await deletePost(id, activeCompany);
        setPostsMap((prev) => ({ ...prev, [activeCompany]: (prev[activeCompany] || []).filter((p) => p.id !== id) }));
        toast.success("Gelöscht", { description: "Post wurde entfernt." });
      } else {
        await deleteMember(id, activeCompany);
        setMembersMap((prev) => ({ ...prev, [activeCompany]: (prev[activeCompany] || []).filter((m) => m.id !== id) }));
        toast.success("Gelöscht", { description: "Mitglied wurde entfernt." });
      }
    } catch {
      toast.error("Fehler", { description: "Löschen fehlgeschlagen." });
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">CMS Admin</h1>
          <p className="text-sm text-muted-foreground">
            Verwalte Beiträge und Team-Mitglieder über eine klare, schnelle Oberfläche.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Building className="h-8 w-8 text-muted-foreground" />
          <Select value={activeCompany} onValueChange={setActiveCompany}>
            <SelectTrigger className="w-[280px] text-xl p-6">
              <SelectValue placeholder="Firma wählen" />
            </SelectTrigger>
            <SelectContent>
              {companies.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" /> Beiträge
            </CardTitle>
            <Badge variant="secondary">{posts.length}</Badge>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-sm text-muted-foreground">für {activeCompany}</CardDescription>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" /> Team
            </CardTitle>
            <Badge variant="secondary">{teamMembers.length}</Badge>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-sm text-muted-foreground">für {activeCompany}</CardDescription>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="posts" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="posts">Beiträge</TabsTrigger>
          <TabsTrigger value="members">Team</TabsTrigger>
        </TabsList>

        {/* POSTS */}
        <TabsContent value="posts" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-[1fr,420px]">
            <Card className="min-h-[320px]">
              <CardHeader>
                <CardTitle>Beiträge ({activeCompany})</CardTitle>
                <CardDescription>Liste der vorhandenen Posts. Löschen ist rückfragend abgesichert.</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="flex gap-3">
                        <Skeleton className="h-12 w-12 rounded-lg" />
                        <div className="w-full space-y-2">
                          <Skeleton className="h-4 w-2/3" />
                          <Skeleton className="h-3 w-1/3" />
                          <Skeleton className="h-3 w-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : errorPosts[activeCompany] ? (
                  <div className="text-sm text-destructive">{errorPosts[activeCompany]}</div>
                ) : posts.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    Keine Beiträge vorhanden. Lege den ersten Eintrag an.
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {posts.map((post) => (
                      <li key={post.id} className="rounded-lg border p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{post.title}</span>
                              {post.category ? (
                                <Badge variant="outline" className="gap-1">
                                  <Tag className="h-3 w-3" />
                                  {post.category}
                                </Badge>
                              ) : null}
                            </div>
                            <p className="text-sm text-muted-foreground">{post.description}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEditPost(post)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteTarget({ type: "post", id: post.id, title: post.title })}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Neuen Beitrag erstellen</CardTitle>
                <CardDescription>Pflichtfelder: Titel, Kategorie, Beschreibung.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="post-title">Titel</Label>
                  <Input
                    id="post-title"
                    placeholder="z. B. Neuer Standort eröffnet"
                    value={newPost.title ?? ""}
                    onChange={(e) => setNewPost((p) => ({ ...p, title: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="post-category">Kategorie</Label>
                  <Select
                    value={newPost.category ?? undefined}
                    onValueChange={(val) => setNewPost((p) => ({ ...p, category: val }))}>
                    <SelectTrigger id="post-category">
                      <SelectValue placeholder="Kategorie wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="post-desc">Beschreibung</Label>
                  <Textarea
                    id="post-desc"
                    placeholder="Kurzer Text …"
                    value={newPost.description ?? ""}
                    onChange={(e) => setNewPost((p) => ({ ...p, description: e.target.value }))}
                    className="min-h-[120px]"
                  />
                </div>
                <Separator />
                <div className="flex justify-end">
                  <Button onClick={handleCreatePost} disabled={isSavingPost}>
                    {isSavingPost ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-4 w-4" />
                    )}
                    Post erstellen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* MEMBERS */}
        <TabsContent value="members" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-[1fr,420px]">
            <Card className="min-h-[320px]">
              <CardHeader>
                <CardTitle>Team-Mitglieder ({activeCompany})</CardTitle>
                <CardDescription>Profile mit Standort und Rolle.</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Skeleton className="h-12 w-12 rounded-full" />
                        <div className="w-full space-y-2">
                          <Skeleton className="h-4 w-1/2" />
                          <Skeleton className="h-3 w-1/3" />
                          <Skeleton className="h-3 w-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : errorMembers[activeCompany] ? (
                  <div className="text-sm text-destructive">{errorMembers[activeCompany]}</div>
                ) : teamMembers.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    Keine Team-Mitglieder vorhanden. Lege das erste Profil an.
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {teamMembers.map((member) => (
                      <li key={member.id} className="rounded-lg border p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <Avatar>
                              {member.profileImageUrl ? (
                                <AvatarImage src={member.profileImageUrl} alt={member.name} />
                              ) : (
                                <AvatarFallback>{member.name?.slice(0, 2)?.toUpperCase()}</AvatarFallback>
                              )}
                            </Avatar>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{member.name}</span>
                                <Badge variant="outline">{member.role}</Badge>
                              </div>
                              <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {member.location}
                              </div>
                              {member.quote ? <p className="text-sm italic">„{member.quote}“</p> : null}
                              <p className="text-sm text-muted-foreground">{member.bio}</p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEditMember(member)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteTarget({ type: "member", id: member.id, name: member.name })}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Neues Team-Mitglied</CardTitle>
                <CardDescription>Pflichtfelder: Name, Rolle, Standort, Bio.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="member-name">Name</Label>
                    <Input
                      id="member-name"
                      placeholder="z. B. Alex Example"
                      value={newMember.name ?? ""}
                      onChange={(e) => setNewMember((m) => ({ ...m, name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="member-role">Rolle</Label>
                    <Input
                      id="member-role"
                      placeholder="z. B. Head of Marketing"
                      value={newMember.role ?? ""}
                      onChange={(e) => setNewMember((m) => ({ ...m, role: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="member-location">Standort</Label>
                    <Input
                      id="member-location"
                      placeholder="z. B. Berlin"
                      value={newMember.location ?? ""}
                      onChange={(e) => setNewMember((m) => ({ ...m, location: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="member-bio">Bio</Label>
                    <Textarea
                      id="member-bio"
                      placeholder="Kurzprofil …"
                      value={newMember.bio ?? ""}
                      onChange={(e) => setNewMember((m) => ({ ...m, bio: e.target.value }))}
                      className="min-h-[120px]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="member-quote">Zitat (optional)</Label>
                    <Textarea
                      id="member-quote"
                      placeholder="„Kurzes Motto …“"
                      value={newMember.quote ?? ""}
                      onChange={(e) => setNewMember((m) => ({ ...m, quote: e.target.value }))}
                      className="min-h-[80px]"
                      disabled={isSavingMember}
                    />
                  </div>

                  {/* Bild-Upload */}
                  <ImageUploader
                    id="member-image"
                    label="Profilbild (optional)"
                    hint="PNG, JPG oder WebP • max. 5 MB"
                    value={profileImageFile}
                    onChange={setProfileImageFile}
                    accept={["image/png", "image/jpeg", "image/webp"]}
                    maxSizeMB={5}
                    disabled={isSavingMember}
                  />
                </div>
                <Separator />
                <div className="flex justify-end">
                  <Button onClick={handleCreateMember} disabled={isSavingMember}>
                    {isSavingMember ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-4 w-4" />
                    )}
                    Mitglied erstellen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete-Confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Löschen bestätigen</DialogTitle>
            <DialogDescription>
              {deleteTarget?.type === "post" ? (
                <>
                  Möchtest du den Post <strong>„{deleteTarget?.title ?? "Unbenannt"}”</strong> wirklich löschen?
                </>
              ) : (
                <>
                  Möchtest du das Mitglied <strong>„{deleteTarget?.name ?? "Unbenannt"}”</strong> wirklich löschen?
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
            <Button className="bg-destructive hover:bg-destructive/90" onClick={confirmDelete}>
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Post */}
      <Dialog open={!!editPost} onOpenChange={(open) => !open && setEditPost(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Post bearbeiten</DialogTitle>
            <DialogDescription>Felder anpassen und speichern.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="edit-post-title">Titel</Label>
              <Input
                id="edit-post-title"
                value={editPostForm.title ?? ""}
                onChange={(e) => setEditPostForm((f) => ({ ...f, title: e.target.value }))}
                disabled={isUpdatingPost}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-post-category">Kategorie</Label>
              <Select
                value={editPostForm.category ?? undefined}
                onValueChange={(val) => setEditPostForm((f) => ({ ...f, category: val }))}
                disabled={isUpdatingPost}>
                <SelectTrigger id="edit-post-category">
                  <SelectValue placeholder="Kategorie wählen" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-post-desc">Beschreibung</Label>
              <Textarea
                id="edit-post-desc"
                value={editPostForm.description ?? ""}
                onChange={(e) => setEditPostForm((f) => ({ ...f, description: e.target.value }))}
                className="min-h-[120px]"
                disabled={isUpdatingPost}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditPost(null)} disabled={isUpdatingPost}>
              Abbrechen
            </Button>
            <Button onClick={handleUpdatePost} disabled={isUpdatingPost}>
              {isUpdatingPost ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member */}
      <Dialog open={!!editMember} onOpenChange={(open) => !open && setEditMember(null)}>
        <DialogContent className="max-w-[720px]">
          <DialogHeader>
            <DialogTitle>Mitglied bearbeiten</DialogTitle>
            <DialogDescription>Profilangaben ändern, optional Bild ersetzen oder entfernen.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Linke Spalte */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="edit-member-name">Name</Label>
                <Input
                  id="edit-member-name"
                  value={editMemberForm.name ?? ""}
                  onChange={(e) => setEditMemberForm((f) => ({ ...f, name: e.target.value }))}
                  disabled={isUpdatingMember}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-member-role">Rolle</Label>
                <Input
                  id="edit-member-role"
                  value={editMemberForm.role ?? ""}
                  onChange={(e) => setEditMemberForm((f) => ({ ...f, role: e.target.value }))}
                  disabled={isUpdatingMember}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-member-location">Standort</Label>
                <Input
                  id="edit-member-location"
                  value={editMemberForm.location ?? ""}
                  onChange={(e) => setEditMemberForm((f) => ({ ...f, location: e.target.value }))}
                  disabled={isUpdatingMember}
                />
              </div>
            </div>

            {/* Rechte Spalte */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="edit-member-bio">Bio</Label>
                <Textarea
                  id="edit-member-bio"
                  value={editMemberForm.bio ?? ""}
                  onChange={(e) => setEditMemberForm((f) => ({ ...f, bio: e.target.value }))}
                  className="min-h-[100px]"
                  disabled={isUpdatingMember}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-member-quote">Zitat (optional)</Label>
                <Textarea
                  id="edit-member-quote"
                  value={editMemberForm.quote ?? ""}
                  onChange={(e) => setEditMemberForm((f) => ({ ...f, quote: e.target.value }))}
                  className="min-h-[80px]"
                  disabled={isUpdatingMember}
                />
              </div>
            </div>
          </div>

          {/* Bildbereich */}
          <div className="grid gap-4 md:grid-cols-[220px,1fr]">
            <div className="flex items-center gap-3">
              <Avatar className="h-16 w-16">
                {editMember?.profileImageUrl ? (
                  <AvatarImage src={editMember.profileImageUrl} alt={editMember?.name} />
                ) : (
                  <AvatarFallback>{editMember?.name?.slice(0, 2)?.toUpperCase()}</AvatarFallback>
                )}
              </Avatar>
              <div className="text-xs text-muted-foreground">
                Aktuelles Bild (falls vorhanden). Du kannst rechts ein neues wählen
                {editMember?.profileImageUrl ? " oder das bestehende entfernen." : "."}
              </div>
            </div>

            <div className="space-y-2">
              <ImageUploader
                id="edit-member-image"
                label="Profilbild ersetzen (optional)"
                hint="PNG, JPG oder WebP • max. 5 MB"
                value={editMemberFile}
                onChange={(file) => {
                  setEditMemberFile(file);
                  if (file) setRemoveImage(false);
                }}
                accept={["image/png", "image/jpeg", "image/webp"]}
                maxSizeMB={5}
                disabled={isUpdatingMember || removeImage}
              />

              {editMember?.profileImageUrl ? (
                <div className="flex items-center gap-2 pt-1">
                  <Switch
                    id="remove-member-image"
                    checked={removeImage}
                    onCheckedChange={(checked) => {
                      setRemoveImage(checked);
                      if (checked) setEditMemberFile(null);
                    }}
                    disabled={isUpdatingMember || !!editMemberFile}
                  />
                  <Label htmlFor="remove-member-image" className="text-sm">
                    Bestehendes Bild entfernen
                  </Label>
                </div>
              ) : null}

              {editMemberFile ? (
                <p className="text-xs text-muted-foreground">Neues Bild ausgewählt – das bestehende wird ersetzt.</p>
              ) : null}
              {removeImage ? <p className="text-xs text-muted-foreground">Das bestehende Bild wird entfernt.</p> : null}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditMember(null)} disabled={isUpdatingMember}>
              Abbrechen
            </Button>
            <Button onClick={handleUpdateMember} disabled={isUpdatingMember}>
              {isUpdatingMember ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CmsAdminPanel;
